import { omit } from "lodash";
import { getFromDataPath, mutateByPath } from "../../../utility";
import { ExpressionLogicalOperatorEnum, SubClauseWithCondition } from "../../ars_ldm";
import { isWhereClause, isWhereClauseWithCondition } from "../../typeGuards";
import { ArsContextState, IArsActionAddExtraWhereClause } from "../interface";
import { addPatchToStack } from "./arsc_patchHelpers";

/**
 * Adds an additional where clause to an existing where clause path.
 *
 * @param state - Current ARS context state
 * @param action - Add-extra-where-clause action
 * @returns Updated state with a new clause appended
 */
export const arsc_addExtraWhereClause = <T>(
  state: ArsContextState<T>,
  action: IArsActionAddExtraWhereClause,
): ArsContextState<T> => {
  const whereClause = getFromDataPath(action.dataPath, state, isWhereClause);
  if (!whereClause) {
    throw new Error(`Invalid dataPath for ADD_EXTRA_WHERE_CLAUSE: ${action.dataPath}`);
  }

  if (isWhereClauseWithCondition(whereClause)) {
    const newCompoundExpression = {
      logicalOperator: ExpressionLogicalOperatorEnum.AND,
      whereClauses: [
        {
          id: crypto.randomUUID(),
          subClauseId: "",
          condition: whereClause.condition,
          level: whereClause.level + 1,
          order: 0,
        },
        {
          id: crypto.randomUUID(),
          subClauseId: "",
          condition: {},
          level: whereClause.level + 1,
          order: 1,
        },
      ],
    };
    const newEntity = {
      ...omit(whereClause, "condition"),
      compoundExpression: newCompoundExpression,
      level: whereClause.level,
      order: whereClause.order,
    };

    return addPatchToStack(
      state,
      mutateByPath<ArsContextState<T>>({ ...state, changed: true }, action.dataPath, newEntity),
    );
  }

  const ce = whereClause.compoundExpression;
  const newClause: SubClauseWithCondition = {
    id: crypto.randomUUID(),
    subClauseId: "",
    condition: {},
    level:
      ce.whereClauses?.[0]?.level ??
      // istanbul ignore next
      1,
    order: (
      ce.whereClauses ??
      // istanbul ignore next
      []
    ).length,
  };
  const newWhereClauses = [
    ...(ce.whereClauses ??
      // istanbul ignore next
      []),
    newClause,
  ];
  const newCompoundExpression = {
    ...ce,
    logicalOperator:
      ce.logicalOperator === ExpressionLogicalOperatorEnum.NOT
        ? ExpressionLogicalOperatorEnum.AND
        : ce.logicalOperator,
    whereClauses: newWhereClauses,
  };

  return addPatchToStack(
    state,
    mutateByPath<ArsContextState<T>>(
      { ...state, changed: true },
      `${action.dataPath}/compoundExpression`,
      newCompoundExpression,
    ),
  );
};
